import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Coins, Plus, X, Loader2, Star, Power, Trash2, Pencil } from "lucide-react";

const blank = {
  code: "", name: "", name_ar: "", symbol: "", decimals: 2, is_active: true,
  min_balance: 0, max_balance: 1000000000, min_transfer: 1, max_transfer: 1000000,
  deposit_fee: 0, withdraw_fee: 0, transfer_fee: 0, cross_currency_fee: 0,
};

const numFields = [
  ["decimals", "المنازل العشرية"], ["min_balance", "أدنى رصيد"], ["max_balance", "أعلى رصيد"],
  ["min_transfer", "أدنى تحويل"], ["max_transfer", "أعلى تحويل"], ["deposit_fee", "رسوم إيداع"],
  ["withdraw_fee", "رسوم سحب"], ["transfer_fee", "رسوم تحويل"], ["cross_currency_fee", "رسوم تحويل العملات"],
];

function CurrencyModal({ initial, isEdit, onClose, onSaved }) {
  const { t, direction } = useI18n();
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        const { code, created_at, id, is_base, ...payload } = form;
        await api.put(`/currencies/${form.code}`, payload);
      } else {
        await api.post("/currencies", form);
      }
      toast.success(t('Saved'));
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-2xl ob-card rounded-md p-6 my-8" onClick={(e) => e.stopPropagation()} data-testid="currency-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-bold text-white">{isEdit ? `${t('edit')} ${form.code}` : t("add_currency")}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t('currency_code')} disabled={isEdit} value={form.code} onChange={(v) => set("code", v.toUpperCase())} ltr testid="cur-code" />
          <Field label={t('The_abbreviated_symbol')} value={form.symbol} onChange={(v) => set("symbol", v)} testid="cur-symbol" />
          <Field label={t('Name_English')} value={form.name} onChange={(v) => set("name", v)} ltr testid="cur-name" />
          <Field label={t('Name_Arabic')} value={form.name_ar} onChange={(v) => set("name_ar", v)} testid="cur-name-ar" />
          {numFields.map(([k, label]) => (
            <Field key={k} label={label} type="number" value={form[k]} onChange={(v) => set(k, parseFloat(v) || 0)} ltr testid={`cur-${k}`} />
          ))}
        </div>
        <label className="flex items-center gap-2 mt-4 text-sm text-zinc-300">
          <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} className="accent-[#00E5FF]" /> {t('Activated')}
        </label>
        <button onClick={save} disabled={busy} data-testid="save-currency-button"
          className="mt-6 w-full bg-[#00E5FF] text-black font-bold py-3 rounded-md hover:bg-[#33EBFF] flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 size={18} className="animate-spin" />} {t('save')}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", ltr, disabled, testid }) {
  return (
    <div>
      <label className="text-xs text-zinc-500">{label}</label>
      <input type={type} value={value} disabled={disabled} dir={ltr ? "ltr" : "rtl"} data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full bg-[#121212] border border-zinc-800 rounded-md px-3 py-2 text-white text-sm ${ltr ? "text-end" : ""} focus:border-[#00E5FF] outline-none disabled:opacity-50`} />
    </div>
  );
}

export default function AdminCurrencies() {
  const { t, direction } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/currencies"); setItems(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setBase = async (code) => {
    try { await api.post(`/currencies/${code}/set-base`); toast.success(`${code} ${t("became_default_currency")}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleActive = async (c) => {
    try { await api.put(`/currencies/${c.code}`, { is_active: !c.is_active }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (code) => {
    if (!window.confirm(`${t("delete_currency")} ${code}?`)) return;
    try { await api.delete(`/currencies/${code}`); toast.success(t('Deleted')); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="w-48 ob-loader h-1 rounded-full bg-zinc-900 mx-auto mt-20" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3"><Coins className="text-[#00E5FF]" />{t('Currency_Management')}</h1>
          <p className="text-zinc-500 text-sm mt-1">{t('Add_modify_and_activate_currencies_set_the_base_currency_and_fees')}</p>
        </div>
        <button onClick={() => setModal({ data: { ...blank }, edit: false })} data-testid="add-currency-button"
          className="bg-[#00E5FF] text-black font-bold px-5 py-2.5 rounded-md hover:bg-[#33EBFF] flex items-center gap-2"><Plus size={18} />{t('cur.new')}</button>
      </div>

      <div className="ob-card rounded-md overflow-x-auto">
        <table
          dir={direction}
          className="w-full table-auto border-collapse text-sm"
          data-testid="currencies-table"
        >
          <thead className="bg-[#111111] sticky top-0">
            <tr className="border-b border-zinc-800 text-zinc-400 text-xs uppercase">
              <th className="px-6 py-4 whitespace-nowrap text-start">{t('common.currency')}</th>
              <th className="px-6 py-4 whitespace-nowrap text-start">{t('common.status')}</th>
              <th className="px-6 py-4 whitespace-nowrap text-center">{t('Decimal')}</th>
              <th className="px-6 py-4 whitespace-nowrap text-center">
                {t('tr.fee')}
              </th>
              <th className="px-6 py-4 whitespace-nowrap text-center">
                {t('common.actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr
                key={c.code}
                className="border-b border-zinc-900 hover:bg-white/[0.03] transition-colors duration-200"
                data-testid={`currency-row-${c.code}`}
              >
                <td className="px-6 py-5">
                  <div className="flex flex-col">
                    <span
                      className="font-mono-ob text-[#00E5FF] font-bold text-base"
                      dir="ltr"
                    >
                      {c.code}
                    </span>

                    <span className="text-xs text-zinc-500 mt-1">
                      {direction === "rtl" ? c.name_ar : c.name}
                    </span>
                  </div>
                </td>

                <td className="px-6 py-5">
                  <div className="flex flex-wrap items-center gap-2">
                    {c.is_base && (
                      <span className="px-2 py-1 rounded-full bg-cyan-500/10 text-cyan-400 text-[11px]">
                        {t('cur.base')}
                      </span>
                    )}

                    <span
                      className={`px-2 py-1 rounded-full text-[11px] ${c.is_active
                          ? "bg-emerald-500/10 text-emerald-400"
                          : "bg-zinc-800 text-zinc-500"
                        }`}
                    >
                      {c.is_active ? t('common.active') : t('common.frozen')}
                    </span>
                  </div>
                </td>

                <td
                  className="px-6 py-5 text-center font-semibold text-zinc-300"
                  dir="ltr"
                >
                  {c.decimals}
                </td>

                <td className="px-6 py-5">
                  <div
                    className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs font-mono-ob"
                    dir="ltr"
                  >
                    <span className="text-zinc-500">{t('Deposit')}</span>
                    <span>{c.deposit_fee}</span>

                    <span className="text-zinc-500">{t('Withdraw')}</span>
                    <span>{c.withdraw_fee}</span>

                    <span className="text-zinc-500">{t('Transfer')}</span>
                    <span>{c.transfer_fee}</span>

                    <span className="text-zinc-500">{t('Exchange')}</span>
                    <span>{c.cross_currency_fee}</span>
                  </div>
                </td>

                <td className="px-6 py-5">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => setBase(c.code)}
                      title={t('Basic_assignment')}
                      disabled={c.is_base}
                      className={`p-2 rounded-md transition ${c.is_base
                          ? "text-[#00E5FF]"
                          : "text-zinc-500 hover:bg-zinc-800 hover:text-[#00E5FF]"
                        }`}
                      data-testid={`set-base-${c.code}`}
                    >
                      <Star size={16} fill={c.is_base ? "#00E5FF" : "none"} />
                    </button>

                    <button
                      onClick={() => toggleActive(c)}
                      title={t('cards.unfreeze')}
                      className="p-2 rounded-md text-zinc-500 hover:bg-zinc-800 hover:text-amber-400 transition"
                      data-testid={`toggle-${c.code}`}
                    >
                      <Power size={16} />
                    </button>

                    <button
                      onClick={() => setModal({ data: { ...c }, edit: true })}
                      title={t('edit')}
                      className="p-2 rounded-md text-zinc-500 hover:bg-zinc-800 hover:text-white transition"
                      data-testid={`edit-${c.code}`}
                    >
                      <Pencil size={16} />
                    </button>

                    <button
                      onClick={() => remove(c.code)}
                      disabled={c.is_base}
                      title={t('Deleted')}
                      className="p-2 rounded-md text-zinc-500 hover:bg-zinc-800 hover:text-red-400 disabled:opacity-30 transition"
                      data-testid={`delete-${c.code}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && <CurrencyModal initial={modal.data} isEdit={modal.edit} onClose={() => setModal(null)} onSaved={load} />}
    </div>
  );
}
